import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { useDataUser } from '../../context/dataUserContext';
import { ReactComponent as Menu } from "../../assets/icons/menu.svg";
import SvgIcon from '../../utils/Icon';
import { useNavigate } from 'react-router-dom';
import { alertaConfirmacion } from '../../utils/alertas';
import { ReactComponent as SalutioRed } from "../../assets/icons/salutiored.svg";
import { ReactComponent as Teleconsulta } from "../../assets/icons/agendar.svg";
import { ReactComponent as Salir } from "../../assets/icons/salida.svg";
import { ReactComponent as InformacionPersonal } from "../../assets/icons/perfil.svg";
import { ReactComponent as ChatIcon } from "../../assets/icons/Chat2.svg";
import { ReactComponent as Documento } from "../../assets/icons/archivo.svg";
import { ReactComponent as HistoriaClinicaIcon } from "../../assets/icons/Telemedicina.svg";
import { ReactComponent as Notas } from "../../assets/icons/Notas.svg";
import { ReactComponent as Prescribir } from "../../assets/icons/Preescribir.svg";

const HistoriaClinica = lazy(() => import('../Formularios/HistoriaClinica'));
const FormatoEvolucion = lazy(() => import('../Formularios/FormatoEvolucion'));
const FormatoTeleOrientacion = lazy(() => import('../Formularios/FormatoTeleOrientacion'));
const PersonalInfo = lazy(() => import('./PersonalInfoModal'));
const PreescribirForm = lazy(() => import('./PreescribirForm'));
const NotasDeConsulta = lazy(() => import('./NotasConsulta'));
const PatientDoctorDocumentsTable = lazy(() => import('../DoctorComponent/PatientDoctorDocumentsTable'));
const Chat = lazy(() => import('./Chat'));

const FormLoading = () => (
    <div className="flex items-center justify-center p-8 text-gray-500 text-sm">Cargando formulario...</div>
);

const MenuNav = ({ children, isPatient, showResume, inicio, handleFinishCall, appointmentId, patientInfo, isDroxy = false, onBackToAppointment, onBeforeBack }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [selectedForm, setSelectedForm] = useState("");
    const [unreadChatCount, setUnreadChatCount] = useState(0);
    const [floatingChatOpen, setFloatingChatOpen] = useState(true);
    const navigate = useNavigate();
    const { role, service } = useDataUser();

    const isDoctorInCall = role === "doctor" && !isPatient && inicio && !showResume;
    const [chatEnabled, setChatEnabled] = useState(false);

    useEffect(() => {
        if (!isDoctorInCall) {
            setChatEnabled(false);
            return;
        }
        const timer = setTimeout(() => setChatEnabled(true), 2500);
        return () => clearTimeout(timer);
    }, [isDoctorInCall]);

    const openChatPanel = useCallback(() => {
        setSelectedForm("chat");
        setUnreadChatCount(0);
        setFloatingChatOpen(false);
        setMenuOpen(false);
    }, []);

    const handleIncomingChatMessage = useCallback(() => {
        if (selectedForm === "chat") return;
        setUnreadChatCount((count) => count + 1);
        setFloatingChatOpen(true);
    }, [selectedForm]);

    useEffect(() => {
        if (selectedForm === "chat") {
            setUnreadChatCount(0);
        }
    }, [selectedForm]);

    // Función para determinar si es un perfil especial
    const isPerfilEspecial = () => {
        const serviceDoctor = patientInfo?.service;
        
        if (!serviceDoctor) {
            return false;
        }
        
        // Perfiles específicos que pueden usar formatos especiales
        const perfilesEspeciales = [
            // Psicología
            'psicologo', 'psicologia',
            // Nutrición
            'nutricionista', 'nutrición', 'nutricion',
            // Terapias (física, respiratoria, ocupacional, lenguaje)
            'terapia_fisica', 'terapia_física', 'terapeuta_fisico', 'terapeuta_físico',
            'terapeuta_respiratorio', 'terapeuta_ocupacional', 
            'fonoaudiología', 'fonoaudiologia',
            // Trabajo social
            'trabajo_social'
        ];
        
        const esEspecial = perfilesEspeciales.includes(serviceDoctor);
        return esEspecial;
    };

    useEffect(() => {
        if (inicio) {
            setSelectedForm("personalInfo");
        }
    }, [inicio]);

    const handleBack = async () => {
        localStorage.removeItem("notasConsulta");
        localStorage.removeItem("formData");

        // Ejecutar función de limpieza antes de navegar (detener cámara y micrófono)
        if (onBeforeBack) {
            try {
                await onBeforeBack();
            } catch {
                // Continuar con la navegación aunque falle la limpieza
            }
        }

        // Si es Droxy, usar la función específica
        if (isDroxy && onBackToAppointment) {
            return onBackToAppointment();
        }

        switch (role) {
            case 'doctor':
                return navigate('/doctor/configuracion-citas');
            case 'patient':
                return navigate('/patient/teleconsultas');
            default:
                return navigate('/');
        }
    };

    const handleBackClick = async () => {
        const result = await alertaConfirmacion({
            titulo: '¿Salir de la videollamada?',
            messageHtml: `
                <div class="swal-confirm-box">
                    <div style="margin-bottom:10px;">
                        <span class="swal-confirm-chip">La llamada NO se finaliza</span>
                    </div>
                    <div class="swal-confirm-subtle">
                        Saldrás de esta pantalla, pero el paciente seguirá en espera y podrás volver a entrar a la consulta.
                    </div>
                </div>
                <div style="margin-top:12px; font-weight:700; color:#0f172a;">
                    ¿Deseas salir ahora?
                </div>
            `,
            confirmButtonText: 'Sí, salir',
            cancelButtonText: 'Permanecer',
            icon: 'warning'
        });

        if (result.isConfirmed) {
            handleBack();
        }
    };

    return (
        <div className="flex h-screen bg-gray-200">
            {/* MENÚ LATERAL */}
            {role === "doctor" && !isPatient && (
                <div>
                    {menuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setMenuOpen(false)} />}

                    <div
                        className={`fixed top-0 left-0 h-full bg-[#205087ff] w-fit shadow-lg z-50
                 transition-transform duration-300 ease-in-out ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}
                    >
                        <ul className="">
                            <li>
                                <button
                                    className={`flex w-full items-center p-5 text-wrap text-white hover:text-[#205087ff] hover:bg-[#f79865ff] hover:font-semibold
                                            ${selectedForm === "personalInfo" && "bg-[#f79865ff] !text-[#205087ff] font-semibold"}`}
                                    onClick={() => {
                                        setSelectedForm("personalInfo");
                                        setMenuOpen(false); // Cerrar el menú al seleccionar esta opción
                                    }}
                                >
                                    <SvgIcon Icon={InformacionPersonal} className="w-6 h-6 mr-5" /> Información Personal
                                </button>
                            </li>

                            <li>
                                <button
                                    className={`flex w-full items-center p-5 text-wrap text-white hover:text-[#205087ff] hover:bg-[#f79865ff] hover:font-semibold
                                            ${selectedForm === "form1" && "bg-[#f79865ff] !text-[#205087ff] font-semibold"}`}
                                    onClick={() => {
                                        setSelectedForm("form1");
                                        setMenuOpen(false); // Cerrar el menú al seleccionar esta opción
                                    }}
                                >
                                    <SvgIcon Icon={HistoriaClinicaIcon} className="w-6 h-6 mr-5" /> Historia Clínica
                                </button>
                            </li>
                            
                            {/* Formatos especiales para perfiles específicos */}
                            {isPerfilEspecial() && (
                                <>
                                    <li>
                                        <button
                                            className={`flex w-full items-center p-5 text-wrap text-white hover:text-[#205087ff] hover:bg-[#f79865ff] hover:font-semibold
                                                    ${selectedForm === "formatoEvolucion" && "bg-[#f79865ff] !text-[#205087ff] font-semibold"}`}
                                            onClick={() => {
                                                setSelectedForm("formatoEvolucion");
                                                setMenuOpen(false);
                                            }}
                                        >
                                            <SvgIcon Icon={HistoriaClinicaIcon} className="w-6 h-6 mr-5" /> Formato de Evolución
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className={`flex w-full items-center p-5 text-wrap text-white hover:text-[#205087ff] hover:bg-[#f79865ff] hover:font-semibold
                                                    ${selectedForm === "formatoTeleOrientacion" && "bg-[#f79865ff] !text-[#205087ff] font-semibold"}`}
                                            onClick={() => {
                                                setSelectedForm("formatoTeleOrientacion");
                                                setMenuOpen(false);
                                            }}
                                        >
                                            <SvgIcon Icon={HistoriaClinicaIcon} className="w-6 h-6 mr-5" /> Formato de Tele Orientación
                                        </button>
                                    </li>
                                </>
                            )}
                            <li>
                                <button
                                    className={`flex w-full items-center p-5 text-wrap text-white hover:text-[#205087ff] hover:bg-[#f79865ff] hover:font-semibold
                                            ${selectedForm === "documentos" && "bg-[#f79865ff] !text-[#205087ff] font-semibold"}`}
                                    onClick={() => {
                                        setSelectedForm("documentos");
                                        setMenuOpen(false); // Cerrar el menú al seleccionar esta opción
                                    }}
                                >
                                    <SvgIcon Icon={Documento} className="w-6 h-6 mr-5" /> Documentos
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`flex w-full items-center p-5 text-wrap text-white hover:text-[#205087ff] hover:bg-[#f79865ff] hover:font-semibold relative
                                            ${selectedForm === "chat" && "bg-[#f79865ff] !text-[#205087ff] font-semibold"}`}
                                    onClick={openChatPanel}
                                >
                                    <SvgIcon Icon={ChatIcon} className="w-6 h- mr-5" /> Chat
                                    {unreadChatCount > 0 && selectedForm !== "chat" && (
                                        <span className="absolute top-3 right-3 min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                                            {unreadChatCount > 9 ? "9+" : unreadChatCount}
                                        </span>
                                    )}
                                </button>
                            </li>

                            <li>
                                <button
                                    className={`flex w-full items-center p-5 text-wrap text-white hover:text-[#205087ff] hover:bg-[#f79865ff] hover:font-semibold
                                            ${selectedForm === "notasDeConsulta" && "bg-[#f79865ff] !text-[#205087ff] font-semibold"}`}
                                    onClick={() => {
                                        setSelectedForm("notasDeConsulta");
                                        setMenuOpen(false); // Cerrar el menú al seleccionar esta opción
                                    }}
                                >
                                    <SvgIcon Icon={Notas} className="w-6 h-6 mr-3" /> Notas de Consulta
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`flex w-full items-center p-5 text-wrap text-white hover:text-[#205087ff] hover:bg-[#f79865ff] hover:font-semibold
                                            ${selectedForm === "preescribirForm" && "bg-[#f79865ff] !text-[#205087ff] font-semibold"}`}
                                    onClick={() => {
                                        setSelectedForm("preescribirForm");
                                        setMenuOpen(false); // Cerrar el menú al seleccionar esta opción
                                    }}
                                >
                                    <SvgIcon Icon={Prescribir} className="w-6 h-6 mr-3" /> Preescribir
                                </button>
                            </li>
                        </ul>
                        <div className="flex flex-col w-full absolute bottom-2 justify-center items-center text-white">
                            <SvgIcon Icon={SalutioRed} className="w-7 h-7 text-white" />
                            <span className="text-xs">Redplika©2026</span>
                        </div>
                    </div>
                </div>
            )}

            {/* MENU SUPERIOR */}
            <section className="w-full h-fit overflow-y-auto">

                <nav className="sticky top-0 flex w-full bg-white p-4 items-center justify-between z-10">

                    {role === "doctor" && !isPatient && !showResume && inicio && (
                        <span className="bg-[#1f94c2ff] p-1 rounded-md cursor-pointer">
                            <SvgIcon
                                Icon={Menu}
                                className="w-7 h-7 text-white"
                                onClick={() => setMenuOpen(!menuOpen)}
                            />
                        </span>
                    )}

                    <li className="hidden md:flex items-center font-semibold text-[#205087ff]">
                        <SvgIcon Icon={Teleconsulta} className="flex items-certer w-6 h-6 mr-5" /> Tele Consulta
                    </li>

                    {role === "doctor" && !isPatient && !showResume && inicio && (
                        <button
                            type="button"
                            onClick={() => {
                                if (selectedForm === "chat") {
                                    setSelectedForm("personalInfo");
                                    setFloatingChatOpen(true);
                                } else {
                                    openChatPanel();
                                }
                            }}
                            className="relative flex items-center gap-2 bg-[#1f94c2ff] hover:bg-[#187a9e] text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
                            title="Abrir chat con el paciente"
                        >
                            <SvgIcon Icon={ChatIcon} className="w-5 h-5" />
                            Chat
                            {unreadChatCount > 0 && selectedForm !== "chat" && (
                                <span className="absolute -top-2 -right-2 min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center animate-pulse">
                                    {unreadChatCount > 9 ? "9+" : unreadChatCount}
                                </span>
                            )}
                        </button>
                    )}

                    <li className="flex items-center space-x-3">
                        {/* Botón Finalizar llamada movido a los controles inferiores en VideoCallAWS */}
                        
                        {/* Botón de colgar para pacientes Droxy - REMOVIDO: ahora se usa el botón de configuración en los controles */}
                        {/* {isDroxy && !showResume && inicio && (
                            <button
                                className="bg-red-500 text-white rounded-md px-2 py-1"
                                onClick={handleFinishCall}
                            >
                                Colgar
                            </button>
                        )} */}

                        <span className="border-r border-2 border-[#f79865ff] h-10" />

                        {/* Ocultar botón Volver para pacientes - ahora usan el botón de colgar en los controles */}
                        {!isPatient && !showResume && (
                            <button
                                className="flex items-center text-[#205087ff]"
                                onClick={handleBackClick}
                            >
                                <SvgIcon Icon={Salir} className="w-6 h-6" /> {isDroxy ? 'Volver a Cita' : 'Volver'}
                            </button>
                        )}
                        {!isPatient && showResume && (
                            <button
                                className="bg-red-500 text-white rounded-md px-2 py-1"
                                onClick={() => navigate("/doctor")}
                            >
                                Salir
                            </button>
                        )}
                    </li>
                </nav>

                {/* CONTENIDO PRINCIPAL DEBAJO DEL NAVBAR */}
                <main id="principal-content" className="flex flex-col md:flex-row w-full h-auto p-4 gap-2">
                    {/* Información personal */}
                    {role === "doctor" && !isPatient && selectedForm === "personalInfo" && (
                        <div className="w-full max-h-[85vh]">
                            <Suspense fallback={<FormLoading />}>
                                <PersonalInfo appointmentId={appointmentId} patientInfo={patientInfo} />
                            </Suspense>
                        </div>
                    )}
                    {/* Historia clínica */}
                    {role === "doctor" && !isPatient && selectedForm === "form1" && (
                        <div className="w-full max-h-[85vh]">
                            <Suspense fallback={<FormLoading />}>
                                <HistoriaClinica patientInfo={patientInfo} appointmentId={appointmentId} />
                            </Suspense>
                        </div>
                    )}
                    
                    {/* Formato de Evolución */}
                    {role === "doctor" && !isPatient && selectedForm === "formatoEvolucion" && (
                        <div className="w-full max-h-[85vh]">
                            <Suspense fallback={<FormLoading />}>
                                <FormatoEvolucion appointmentInfo={patientInfo} appointmentId={appointmentId} />
                            </Suspense>
                        </div>
                    )}
                    
                    {/* Formato de Tele Orientación */}
                    {role === "doctor" && !isPatient && selectedForm === "formatoTeleOrientacion" && (
                        <div className="w-full max-h-[85vh]">
                            <Suspense fallback={<FormLoading />}>
                                <FormatoTeleOrientacion appointmentInfo={patientInfo} appointmentId={appointmentId} />
                            </Suspense>
                        </div>
                    )}
                    {/* Documentos */}
                    {role === "doctor" && !isPatient && selectedForm === "documentos" && (
                        <div className="w-full overflow-x-auto max-h-[85vh]">
                            <Suspense fallback={<FormLoading />}>
                                <PatientDoctorDocumentsTable
                                    patientId={patientInfo && patientInfo.patientId}
                                />
                            </Suspense>
                        </div>
                    )}
                    {/* Chat doctor: se monta tras un breve delay para no competir con el join de Chime */}
                    {isDoctorInCall && chatEnabled && (
                        <>
                            <div
                                className={
                                    selectedForm === "chat"
                                        ? `w-full max-h-[85vh] min-h-[320px] ${!(selectedForm === "chat" || floatingChatOpen) ? "hidden" : ""}`
                                        : `fixed bottom-4 right-4 z-[60] w-[min(100vw-2rem,320px)] h-[min(42vh,380px)] flex flex-col shadow-2xl rounded-lg overflow-hidden border border-[#1f94c2ff] ${!floatingChatOpen ? "hidden" : ""}`
                                }
                            >
                                {selectedForm !== "chat" && floatingChatOpen && (
                                    <div className="flex items-center justify-between bg-[#1f94c2ff] text-white px-3 py-2 shrink-0">
                                        <span className="text-sm font-semibold flex items-center gap-2">
                                            <SvgIcon Icon={ChatIcon} className="w-4 h-4" />
                                            Chat con paciente
                                            {unreadChatCount > 0 && (
                                                <span className="bg-red-500 text-xs px-2 py-0.5 rounded-full">
                                                    {unreadChatCount} nuevo{unreadChatCount !== 1 ? "s" : ""}
                                                </span>
                                            )}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={openChatPanel}
                                                className="text-xs underline hover:text-orange-200"
                                            >
                                                Ampliar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setFloatingChatOpen(false);
                                                    setUnreadChatCount(0);
                                                }}
                                                className="text-xs bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded"
                                            >
                                                Ocultar
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <div className={selectedForm === "chat" ? "h-full" : "flex-1 min-h-0 bg-white"}>
                                    <Suspense fallback={<FormLoading />}>
                                        <Chat
                                            appointmentId={appointmentId}
                                            compact={selectedForm !== "chat"}
                                            onIncomingMessage={handleIncomingChatMessage}
                                            isChatFocused={selectedForm === "chat" || unreadChatCount === 0}
                                            className={selectedForm !== "chat" ? "border-0 rounded-none shadow-none h-full" : ""}
                                        />
                                    </Suspense>
                                </div>
                            </div>
                            {!floatingChatOpen && selectedForm !== "chat" && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFloatingChatOpen(true);
                                        setUnreadChatCount(0);
                                    }}
                                    className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 bg-[#1f94c2ff] hover:bg-[#187a9e] text-white px-4 py-3 rounded-full shadow-lg font-medium"
                                >
                                    <SvgIcon Icon={ChatIcon} className="w-5 h-5" />
                                    Chat
                                    {unreadChatCount > 0 && (
                                        <span className="bg-red-500 text-xs min-w-[1.25rem] h-5 px-1 rounded-full flex items-center justify-center animate-pulse">
                                            {unreadChatCount > 9 ? "9+" : unreadChatCount}
                                        </span>
                                    )}
                                </button>
                            )}
                        </>
                    )}
                    {/* Notas de consulta */}
                    {role === "doctor" && !isPatient && selectedForm === "notasDeConsulta" && (
                        <div className="w-full max-h-[85vh]">
                            <Suspense fallback={<FormLoading />}>
                                <NotasDeConsulta appointmentId={appointmentId} />
                            </Suspense>
                        </div>
                    )}
                    {/* Preescribir */}
                    {role === "doctor" && !isPatient && selectedForm === "preescribirForm" && (
                        <div className="w-full overflow-x-auto max-h-[85vh]">
                            <Suspense fallback={<FormLoading />}>
                                <PreescribirForm appointmentId={appointmentId} />
                            </Suspense>
                        </div>
                    )}

                    {(role === "patient" || isPatient) && (
                        <div className="w-full h-[85vh]">
                            <Suspense fallback={<FormLoading />}>
                                <Chat appointmentId={appointmentId} />
                            </Suspense>
                        </div>
                    )}

                    {children}
                </main>
            </section>
        </div>
    )
}

export default MenuNav